import { Options as RichTextOptions } from "@contentful/rich-text-react-renderer"
import { BLOCKS, MARKS } from "@contentful/rich-text-types"
import React, { useContext } from "react"
import { css } from "styled-components"
import { BackgroundColourContext } from "../../util/background-colour-context"
import parseTextColour, {
  TParseTextColourOptions,
} from "../../util/parse-text-colour"
import { BUTTON_TYPENAME, ContentfulButton } from "../button"
import VariantContainer from "../variant-container"
import {
  Bold,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Italic,
  Paragraph,
  StaticImage,
  Underline,
} from "./rich-text.style"

type TTextColourOptions = {
  darkModeColour?: string
  lightModeColour?: string
} & TParseTextColourOptions

/** Sets default text colour based on background then adds colour highlights */
const setTextColour = (
  children: React.ReactNode,
  options?: TTextColourOptions
) => {
  const defaultOptions = {
    darkModeColour: `var(--smoke)`,
    lightModeColour: `var(--body-text-grey)`,
  }
  const {
    state: { dark },
  } = useContext(BackgroundColourContext)

  return (
    <span
      style={{
        color: dark
          ? options?.darkModeColour || defaultOptions.darkModeColour
          : options?.lightModeColour || defaultOptions.lightModeColour,
      }}
    >
      {parseTextColour(children, options)}
    </span>
  )
}

export const DefaultRichTextOptions: RichTextOptions = {
  renderMark: {
    [MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
    [MARKS.ITALIC]: (text) => <Italic>{text}</Italic>,
    [MARKS.UNDERLINE]: (text) => <Underline>{text}</Underline>,
  },
  renderNode: {
    [BLOCKS.HEADING_1]: (node, children) => (
      <Heading1>
        {setTextColour(children, {
          lightModeColour: `var(--slate)`,
          darkModeColour: `var(--white)`,
        })}
      </Heading1>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <Heading2>
        {setTextColour(children, {
          lightModeColour: `var(--slate)`,
          darkModeColour: `var(--white)`,
          // Specifically for H2, if a chunk of text is highlighted with
          // colour we want the remaining text to be lighter weight
          colourStyle: { fontWeight: 500 },
          noColourStyle: { fontWeight: 400 },
        })}
      </Heading2>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <Heading3>
        {setTextColour(children, {
          lightModeColour: `var(--slate)`,
          darkModeColour: `var(--white)`,
        })}
      </Heading3>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <Heading4>
        {setTextColour(children, {
          lightModeColour: `var(--slate)`,
          darkModeColour: `var(--white)`,
        })}
      </Heading4>
    ),
    [BLOCKS.HEADING_5]: (node, children) => (
      <Heading5>
        {setTextColour(children, {
          lightModeColour: `var(--slate)`,
          darkModeColour: `var(--white)`,
        })}
      </Heading5>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <Paragraph>{setTextColour(children)}</Paragraph>
    ),
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const component = node?.data?.target
      if (component) {
        switch (component.__typename) {
          case BUTTON_TYPENAME:
            return (
              <VariantContainer
                variants={[
                  css`
                    display: grid;
                    justify-items: center;
                    margin-top: 32px;
                  `,
                  css`
                    margin-top: 48px;
                  `,
                  css`
                    justify-items: left;
                  `,
                ]}
              >
                <ContentfulButton {...component} />
              </VariantContainer>
            )
        }
      }
      return null
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const asset = node?.data?.target
      if (asset) {
        switch (asset.file.contentType) {
          case "image/svg+xml":
            return (
              <StaticImage
                src={asset.file.url}
                alt={asset?.description || ""}
              />
            )
          default:
            return null
        }
      }
      return null
    },
  },
}
